"use client";

import React from "react";
import type { Contacts } from "@/src/gql/graphql";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";

// Import contact detail config
import { getEntityDefinition } from "@/lib/entity-registry";

// Import contact detail tab components
import { ContactDetailTabComponents } from "@/features/contacts/components/ContactDetailTabComponents";

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in the detail config (incl. the dynamic Sequence tab) can never
// silently fall through to the default.
const CONTACT_TAB_COMPONENTS = ContactDetailTabComponents as Record<
  string,
  React.ComponentType<TabContentProps<Contacts>>
>;

function resolveContactTabComponent(
  tab: TabConfig<Contacts> | undefined
): React.ComponentType<TabContentProps<Contacts>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && CONTACT_TAB_COMPONENTS[custom]) {
    return CONTACT_TAB_COMPONENTS[custom];
  }
  return CONTACT_TAB_COMPONENTS.ContactDetailsDisplay;
}
import { useSequencesAccess } from "@/lib/features/use-sequences-access";
import { MiniTimeline } from "@/features/contacts/components/MiniTimeline";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";
import { InheritedContactMethodValue } from "@/components/shared/ContactMethodActions/InheritedContactMethodValue";
import { resolveInheritedContactMethods } from "@/lib/contacts/company-line/inherited-methods";

// Import inline email composer components
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import type { ComposeMessageModalData } from "@/lib/modals/data/modal-data-types";

// ============================================================================
// SEND EMAIL ACTION COMPONENT
// ============================================================================

interface SendEmailActionProps {
  contact: Contacts;
}

const SendEmailAction: React.FC<SendEmailActionProps> = ({ contact }) => {
  const { openModal } = useGlobalModalStack();

  const handleSendEmail = React.useCallback(() => {
    openModal(MODAL_IDS.messages.compose, {
      item: contact,
      customerEmail: contact.email,
      customerName: contact.name,
    } as ComposeMessageModalData);
  }, [openModal, contact]);

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Send Email</h3>
      </div>
      <Button
        onClick={handleSendEmail}
        variant="default"
        size="default"
        className="w-full sm:w-auto"
      >
        <Mail className="h-4 w-4" />
        Compose Email to {contact.name}
      </Button>
    </div>
  );
};

// ============================================================================
// CONTACT DETAIL MODAL TABS HOOK
// ============================================================================

export interface UseContactDetailModalTabsReturn {
  tabs: TabConfig<Contacts>[];
  renderPrimaryArea: (activeTab: string, contact: Contacts) => React.ReactNode;
  renderInfoPanel: (contact: Contacts) => React.ReactNode;
}

export function useContactDetailModalTabs(): UseContactDetailModalTabsReturn {
  // Get tabs from config (static for now)
  const baseTabs = getEntityDefinition("contacts").detailConfig.tabs as unknown as TabConfig<Contacts>[];

  // Sales-cadence tab — appended ONLY for the rollout allowlist so other users
  // don't see an empty tab (frontend-only gate; see lib/features/sequences-access).
  const canViewSequences = useSequencesAccess();
  const tabs: TabConfig<Contacts>[] = canViewSequences
    ? [
        ...baseTabs,
        {
          id: "sequence",
          label: "Sequence",
          icon: "Route",
          content: { type: "custom", customComponent: "ContactSequenceDisplay" },
          group: "core",
          priority: 99,
        } as unknown as TabConfig<Contacts>,
      ]
    : baseTabs;

  // Render primary area based on active tab
  const renderPrimaryArea = (activeTab: string, contact: Contacts): React.ReactNode => {
    // Get the tab config for this tab
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];

    // Minimal props for TabContentProps - components will use context for hooks/callbacks
    const TabComponent = resolveContactTabComponent(tabConfig);
    return <TabComponent item={contact} tab={tabConfig} />;
  };

  // Render info panel (stable across tabs)
  const renderInfoPanel = (contact: Contacts): React.ReactNode => {
    const entityContext = {
      item: contact as unknown as Record<string, unknown>,
      entityType: "contacts",
    };
    // The company's shared front desk / role mailbox, projected onto the person
    // so a contact whose number now belongs to their company is still reachable
    // from this panel. Badged "Company line", read-only, never written back.
    const inherited = resolveInheritedContactMethods(contact, contact.companies);
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <p className="text-gray-900 font-medium">{contact.name}</p>
            </div>
            {contact.email && (
              <div>
                <span className="text-gray-600">Email:</span>
                <p className="text-gray-900">
                  {contact.email.trim() ? (
                    <ContactMethodActions
                      kind="email"
                      value={contact.email.trim()}
                      entityContext={entityContext}
                    />
                  ) : (
                    contact.email
                  )}
                </p>
              </div>
            )}
            {inherited.emails.map((method) => (
              <div key={`inherited-email-${method.value}`}>
                <span className="text-gray-600">Company email:</span>
                <p className="text-gray-900">
                  <InheritedContactMethodValue method={method} fallbackContext={entityContext} />
                </p>
              </div>
            ))}
            {contact.phone && (
              <div>
                <span className="text-gray-600">Phone:</span>
                <p className="text-gray-900">
                  <ContactMethodActions
                    kind="phone"
                    value={String(contact.phone)}
                    entityContext={entityContext}
                  />
                </p>
              </div>
            )}
            {inherited.phones.map((method) => (
              <div key={`inherited-phone-${method.value}`}>
                <span className="text-gray-600">Company phone:</span>
                <p className="text-gray-900">
                  <InheritedContactMethodValue method={method} fallbackContext={entityContext} />
                </p>
              </div>
            ))}
            {contact.title && (
              <div>
                <span className="text-gray-600">Title:</span>
                <p className="text-gray-900">{contact.title}</p>
              </div>
            )}
            {contact.company_name && (
              <div>
                <span className="text-gray-600">Company:</span>
                <p className="text-gray-900">{contact.company_name}</p>
              </div>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h3>
          <MiniTimeline email={contact.email} contactId={contact.id} />
        </div>
      </div>
    );
  };

  return {
    tabs,
    renderPrimaryArea,
    renderInfoPanel,
  };
}
